import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { getGradeColorFromThresholds, isGradeBinVisible } from '../../Utils/gradePalette.js';
import { getLithColorHex } from '../../Utils/lithPalette.js';
import { useLoadingTracker } from '../../contexts/LoadingTrackerContext';
import { getOriginERL } from '../../config/siteConfig';
import SelectedHoleHighlight from '../../clickTools/SelectedHoleHighlight.js';
import {
  isLithCodeVisible,
  resolvedIntervalVisible,
  useHoleDisplayRadii,
  useHoleDisplayStore,
} from '../../store/holeDisplayStore';
import { useGradePaletteStore } from '../../store/gradePaletteStore';
import { fitInstancedMeshBounds } from '../../Utils/instancedBounds';
import { holeTraceColor } from '../../Utils/holeTraceColor';
import {
  desurveyHoleToScene,
  positionAtDepth,
} from '../../Utils/holeDesurvey';
import {
  fetchAllHoleKeys,
  loadMissingAssays,
  loadMissingCollars,
  loadMissingGeology,
  loadMissingSurveys,
} from './loadDatabaseHoles';
import HoleCollarLabels from './HoleCollarLabels';
import { displayKeyFromRow, getHoleRecord, hydrateHoleRecordStore, putCollars } from '../../store/holeRecordStore';

const ORIGIN = () => getOriginERL();

const HOLE_UPLOAD_YIELD_BATCH = 2000;
const INTERVAL_UPLOAD_YIELD_BATCH = 4000;

const yieldToMainThread = () => new Promise((resolve) => setTimeout(resolve, 0));
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const buildUrl = (baseUrl, params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      query.set(key, String(value));
    }
  });
  const qs = query.toString();
  return qs ? `${baseUrl}?${qs}` : baseUrl;
};

const parseResponseRows = async (response, endpointName) => {
  const payload = await response.json();
  if (!response.ok || !payload || payload.success !== true) {
    throw new Error(`[${endpointName}] ${payload?.error || 'Request failed'}`);
  }
  return Array.isArray(payload.data) ? payload.data : [];
};

const isRetryableError = (error) => {
  const errorMessage = error?.message || error?.toString() || String(error) || '';
  const errorString = errorMessage.toLowerCase();
  const errorName = (error?.name || '').toLowerCase();
  const errorType = (error?.type || '').toLowerCase();
  return (
    errorString.includes('http2') ||
    errorString.includes('protocol_error') ||
    errorString.includes('failed to fetch') ||
    errorString.includes('network') ||
    errorString.includes('aborted') ||
    errorString.includes('timeout') ||
    errorString.includes('fetch') ||
    errorName.includes('http2') ||
    errorName.includes('network') ||
    errorName.includes('abort') ||
    errorType === 'networkerror' ||
    errorType === 'abort'
  );
};

const fetchRowsWithRetry = async (url, endpointName, attempt = 0) => {
  try {
    const response = await fetch(url);
    return await parseResponseRows(response, endpointName);
  } catch (error) {
    if (isRetryableError(error) || attempt < 10) {
      const backoffDelay = Math.min(2 ** Math.min(attempt, 5) * 1000, 30000);
      await delay(backoffDelay);
      return fetchRowsWithRetry(url, endpointName, attempt + 1);
    }
    throw error;
  }
};

const getAssayColor = (value, thresholds) => getGradeColorFromThresholds(value, thresholds);

const getLithColor = (lithCode) => new THREE.Color(getLithColorHex(lithCode));

const calculateEndCoordinates = (start, direction, inclination, depth) => {
  direction -= 180;
  const dirRad = (parseFloat(direction) * Math.PI) / 180;
  const incRad = (parseFloat(inclination) * Math.PI) / 180;

  const dx = depth * Math.abs(Math.cos(incRad)) * Math.sin(dirRad);
  const dy = depth * Math.sin(incRad);
  const dz = depth * Math.abs(Math.cos(incRad)) * Math.cos(dirRad);

  return {
    x: start.x - dx,
    y: start.y + dy,
    z: start.z + dz,
  };
};

const createIntervalMatrixFromPoints = (fromPoint, toPoint) => {
  const assayLength = new THREE.Vector3(
    toPoint.x - fromPoint.x,
    toPoint.y - fromPoint.y,
    toPoint.z - fromPoint.z
  ).length();

  const assayPosition = new THREE.Vector3(
    (fromPoint.x + toPoint.x) / 2,
    (fromPoint.y + toPoint.y) / 2,
    (fromPoint.z + toPoint.z) / 2
  );

  const initialRotation = new THREE.Euler(Math.PI / 2, 0, 0);
  const directionRotation = new THREE.Euler().setFromRotationMatrix(
    new THREE.Matrix4().lookAt(
      new THREE.Vector3(fromPoint.x, fromPoint.y, fromPoint.z),
      new THREE.Vector3(toPoint.x, toPoint.y, toPoint.z),
      new THREE.Vector3(0, 1, 0)
    )
  );

  const initialMatrix = new THREE.Matrix4().makeRotationFromEuler(initialRotation);
  const directionMatrix = new THREE.Matrix4().makeRotationFromEuler(directionRotation);
  const combinedMatrix = directionMatrix.multiply(initialMatrix);
  const assayRotation = new THREE.Euler().setFromRotationMatrix(combinedMatrix);
  const assayScale = new THREE.Vector3(1, Math.max(assayLength - 0.05, 0.05), 1);

  return new THREE.Matrix4()
    .makeTranslation(assayPosition.x, assayPosition.y, assayPosition.z)
    .multiply(new THREE.Matrix4().makeRotationFromEuler(assayRotation))
    .multiply(new THREE.Matrix4().makeScale(assayScale.x, assayScale.y, assayScale.z));
};

const createIntervalMatrix = (start, azimuth, dip, fromDepth, toDepth) => {
  const fromPoint = calculateEndCoordinates(start, azimuth, dip, fromDepth);
  const toPoint = calculateEndCoordinates(start, azimuth, dip, toDepth);
  return createIntervalMatrixFromPoints(fromPoint, toPoint);
};

const createHoleMatrixFromPoints = (startPoint, endPoint) => {
  const startVector = new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z);
  const endVector = new THREE.Vector3(endPoint.x, endPoint.y, endPoint.z);
  const direction = new THREE.Vector3().subVectors(endVector, startVector);
  const length = Math.max(direction.length(), 0.05);
  const center = new THREE.Vector3().addVectors(startVector, endVector).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  return new THREE.Matrix4().compose(center, quaternion, new THREE.Vector3(1, length, 1));
};

function collarSceneStart(hole) {
  const origin = ORIGIN();
  const start = {
    x: parseFloat(hole.NAT_East) - origin[0],
    y: parseFloat(hole.NAT_RL) - origin[1],
    z: -parseFloat(hole.NAT_North) + origin[2],
    depth: 0,
  };
  if (!Number.isFinite(start.x) || !Number.isFinite(start.y) || !Number.isFinite(start.z)) return null;
  return start;
}

function holePathPoints(hole, { desurvey, surveys }) {
  const start = collarSceneStart(hole);
  if (!start) return null;
  const azimuth = parseFloat(hole.Collar_Azimuth);
  const dip = parseFloat(hole.Collar_Dip);
  const maxDepth = parseFloat(hole.Max_Depth);
  if (!Number.isFinite(azimuth) || !Number.isFinite(dip) || !Number.isFinite(maxDepth)) return null;

  if (desurvey) {
    const points = desurveyHoleToScene(hole, surveys || [], ORIGIN());
    if (points && points.length >= 2) return { start, azimuth, dip, maxDepth, points };
  }
  const end = calculateEndCoordinates(start, azimuth, dip, maxDepth);
  return { start, azimuth, dip, maxDepth, points: [start, { ...end, depth: maxDepth }] };
}

function transformIntervalRows(rows, path, kind) {
  const out = [];
  (rows || []).forEach((row) => {
    const fromDepth = parseFloat(row.Depth_From);
    const toDepth = parseFloat(row.Depth_To);
    if (!Number.isFinite(fromDepth) || !Number.isFinite(toDepth) || toDepth <= fromDepth) return;

    if (kind === 'lith') {
      const hasLith = row.Lith1_Code !== null && row.Lith1_Code !== undefined && String(row.Lith1_Code).trim() !== '';
      if (!hasLith) return;
    } else {
      const grade = parseFloat(row.Au_ppm);
      if (!Number.isFinite(grade) || grade < 0.5) return;
    }

    const fromPoint = positionAtDepth(path.points, fromDepth);
    const toPoint = positionAtDepth(path.points, toDepth);
    const matrix =
      fromPoint && toPoint
        ? createIntervalMatrixFromPoints(fromPoint, toPoint)
        : createIntervalMatrix(path.start, path.azimuth, path.dip, fromDepth, toDepth);
    if (!matrix) return;

    if (kind === 'lith') {
      out.push({
        matrix,
        color: getLithColor(row.Lith1_Code),
        data: {
          holeID: row.Hole_ID,
          holeKey: displayKeyFromRow(row),
          DataSet: row.DataSet,
          from: `${fromDepth} m`,
          to: `${toDepth} m`,
          lith1_code: String(row.Lith1_Code),
          source: 'Geology',
        },
      });
      return;
    }

    const grade = parseFloat(row.Au_ppm);
    out.push({
      matrix,
      color: getAssayColor(grade),
      data: {
        holeID: row.Hole_ID,
        holeKey: displayKeyFromRow(row),
        DataSet: row.DataSet,
        from: `${fromDepth} m`,
        to: `${toDepth} m`,
        grade: `${grade} g/t`,
        gradeValue: grade,
        source: 'Assay',
      },
    });
  });
  return out;
}

function buildHoleFromRecord(dataset, holeId, { desurvey }) {
  const rec = getHoleRecord(dataset, holeId);
  if (!rec?.collar) return null;
  const path = holePathPoints(rec.collar, { desurvey, surveys: rec.surveys });
  if (!path) return null;
  return {
    holeId: rec.collar.Hole_ID,
    holeKey: displayKeyFromRow(rec.collar),
    points: path.points,
    assays: transformIntervalRows(rec.assays, path, 'assay'),
    liths: transformIntervalRows(rec.geology, path, 'lith'),
    data: rec.collar,
  };
}

function rebuildKeysInto(target, keys, desurvey) {
  (keys || []).forEach((row) => {
    const built = buildHoleFromRecord(row.DataSet, row.Hole_ID, { desurvey });
    if (!built) return;
    target[built.holeKey] = built;
  });
  return { ...target };
}

export default function DatabaseHoleRender({
  name,
  dataset,
  holetype,
  interval_mode = 'assay',
  isplan = false,
  coll_url = '/server/drilling_database/get_dh_coll.php',
  assay_url = '/server/drilling_database/get_dh_samp_with_au.php',
  geology_url = '/server/drilling_database/get_dh_geology.php',
  surv_url = '/server/drilling_database/get_dh_surv.php',
  max_rows = 5000000,
  collarQuery = null,
  desurvey = false,
  debug = false,
  visible,
  onLoadComplete,
  onLoadFailed,
}) {
  const [combinedData, setCombinedData] = useState({});
  const [loading, setLoading] = useState(true);
  const lastQueryRef = useRef(null);
  const { startLoading, stopLoading, updateProgress } = useLoadingTracker();
  const showAssay = useHoleDisplayStore((s) => resolvedIntervalVisible(name, s.intervalVisibleByLayer).assay);
  const showLith = useHoleDisplayStore((s) => resolvedIntervalVisible(name, s.intervalVisibleByLayer).lith);
  const showHoleLabels = useHoleDisplayStore((s) => s.showHoleLabels);

  const objectName = name || `DatabaseHoles_${dataset}_${holetype}_${interval_mode}`;

  useEffect(() => {
    const showGrade = !!visible && !isplan && showAssay;
    require('../../store/clickStore.js').useClickStore.getState().setGradeLegend(objectName, 'Au_ppm', showGrade);
    return () => {
      require('../../store/clickStore.js').useClickStore.getState().setGradeLegend(objectName, 'Au_ppm', false);
    };
  }, [objectName, visible, isplan, showAssay]);

  useEffect(() => {
    if (!visible || isplan || !showLith) {
      require('../../store/clickStore.js').useClickStore.getState().setLithLegend(objectName, [], false);
      return undefined;
    }

    const codes = new Set();
    Object.keys(combinedData || {}).forEach((holeId) => {
      const intervals = combinedData[holeId]?.liths || [];
      intervals.forEach((interval) => {
        const code = interval?.data?.lith1_code;
        if (code !== null && code !== undefined && String(code).trim() !== '') {
          codes.add(String(code).trim());
        }
      });
    });

    require('../../store/clickStore.js').useClickStore.getState().setLithLegend(objectName, Array.from(codes), true);
    return () => {
      require('../../store/clickStore.js').useClickStore.getState().setLithLegend(objectName, [], false);
    };
  }, [objectName, visible, isplan, showLith, combinedData]);

  useEffect(() => {
    if (!visible) return undefined;
    const queryKey = JSON.stringify({
      dataset,
      holetype,
      isplan: !!isplan,
      collarQuery: collarQuery || null,
      desurvey: !!desurvey,
      coll_url,
    });
    lastQueryRef.current = queryKey;

    let cancelled = false;
    const isCancelled = () => cancelled || lastQueryRef.current !== queryKey;
    const combined = {};

    const publish = (keys) => {
      if (isCancelled()) return;
      rebuildKeysInto(combined, keys, !!desurvey && !isplan);
      setCombinedData({ ...combined });
    };

    const loadData = async () => {
      setLoading(true);
      startLoading(objectName);
      try {
        await hydrateHoleRecordStore();
        const wantSurveys = !!desurvey && !isplan;
        const ctx = {
          dataset,
          filters: collarQuery,
          isplan,
          isCancelled,
          onProgress: (info) => updateProgress(objectName, info),
        };

        let keys = [];
        if (collarQuery) {
          keys = await fetchAllHoleKeys({
            dataset,
            filters: collarQuery,
            isplan,
            onProgress: ctx.onProgress,
            isCancelled,
          });
        } else {
          const collars = await fetchRowsWithRetry(
            buildUrl(coll_url, { DataSet: dataset, Hole_Type: holetype, IsPlan: isplan ? 1 : 0, max_rows }),
            'get_dh_coll.php'
          );
          putCollars(collars);
          keys = (collars || [])
            .filter((row) => row?.DataSet && row?.Hole_ID)
            .map((row) => ({ DataSet: row.DataSet, Hole_ID: row.Hole_ID }));
        }
        if (isCancelled()) return;

        publish(keys);

        ctx.onCollars = (collars) => {
          publish((collars || []).map((row) => ({ DataSet: row.DataSet, Hole_ID: row.Hole_ID })));
        };
        await loadMissingCollars(keys, ctx);
        if (isCancelled()) return;
        publish(keys);

        if (wantSurveys) {
          ctx.onSurveys = (chunkKeys) => publish(chunkKeys);
          await loadMissingSurveys(keys, ctx);
          if (isCancelled()) return;
          publish(keys);
        }

        if (!isplan) {
          ctx.onAssays = (chunkKeys) => publish(chunkKeys);
          ctx.onGeology = (chunkKeys) => publish(chunkKeys);
          await loadMissingAssays(keys, ctx);
          if (isCancelled()) return;
          await loadMissingGeology(keys, ctx);
          if (isCancelled()) return;
          publish(keys);
        }

        updateProgress(objectName, { phase: 'done', percent: 100, indeterminate: false, loaded: keys.length, total: keys.length });
        if (debug) {
          console.log(`[DatabaseHoleRender] ${objectName}`, { keys: keys.length, rendered: Object.keys(combined).length });
        }
        if (!isCancelled()) setLoading(false);
        stopLoading(objectName);
      } catch (error) {
        if (cancelled) return;
        console.error(`[DatabaseHoleRender] Failed to load ${objectName}`, error);
        setLoading(false);
        stopLoading(objectName);
        onLoadFailed?.(objectName);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      stopLoading(objectName);
    };
  }, [
    visible,
    objectName,
    coll_url,
    assay_url,
    geology_url,
    surv_url,
    dataset,
    holetype,
    interval_mode,
    max_rows,
    isplan,
    collarQuery,
    desurvey,
    debug,
    onLoadFailed,
    startLoading,
    stopLoading,
    updateProgress,
  ]);

  useEffect(() => {
    if (!visible) return;
    if (loading) return;
    onLoadComplete?.(objectName);
  }, [visible, loading, objectName, onLoadComplete]);

  const holeCount = Object.keys(combinedData || {}).length;
  if (!visible || (loading && holeCount === 0)) return null;
  if (holeCount === 0) return null;

  const assayCount = countIntervals(combinedData, 'assays');
  const lithCount = countIntervals(combinedData, 'liths');

  return (
    <group visible={visible} name={objectName} userData={{ mdvLayerKey: objectName }}>
      <Holes holes={combinedData} />
      <SelectedHoleHighlight
        holes={combinedData}
        getLinePoints={(id) => {
          const pts = combinedData[id]?.points;
          if (!pts || pts.length < 3) return null;
          return pts.map((p) => [p.x, p.y, p.z]);
        }}
      />
      {!isplan && showAssay && assayCount > 0 && (
        <Intervals count={assayCount} holes={combinedData} field="assays" name={`${objectName}_Assays`} />
      )}
      {!isplan && showLith && lithCount > 0 && (
        <Intervals count={lithCount} holes={combinedData} field="liths" name={`${objectName}_Lithology`} />
      )}
      <HoleCollarLabels holes={combinedData} visible={!!showHoleLabels} />
    </group>
  );
}

function countIntervals(holes, field) {
  let n = 0;
  Object.keys(holes || {}).forEach((id) => {
    n += holes[id]?.[field]?.length || 0;
  });
  return n;
}

function useGrowingInstanceCount(needed) {
  const allocRef = useRef(Math.max(needed, 1));
  const [alloc, setAlloc] = useState(Math.max(needed, 1));
  useEffect(() => {
    const n = Math.max(needed, 1);
    if (n > allocRef.current) {
      const next = Math.max(n, Math.ceil(allocRef.current * 1.5));
      allocRef.current = next;
      setAlloc(next);
    }
  }, [needed]);
  return alloc;
}

function countHoleSegments(holes) {
  let n = 0;
  Object.keys(holes || {}).forEach((id) => {
    const pts = holes[id]?.points;
    n += Math.max((pts?.length || 1) - 1, 0);
  });
  return n;
}

function Holes({ holes }) {
  const ref = useRef();
  const [instancesData, setInstancesData] = useState([]);
  const [colors, setColors] = useState(new Float32Array());
  const { hole: holeRadius } = useHoleDisplayRadii();

  useEffect(() => {
    let cancelled = false;

    const uploadHoles = async () => {
      if (!ref.current) return;
      const holeIds = Object.keys(holes);
      const idata = [];
      const colorsArray = new Float32Array(Math.max(countHoleSegments(holes), 1) * 3);
      const color = new THREE.Color();
      let i = 0;

      for (let holeIdx = 0; holeIdx < holeIds.length; holeIdx += 1) {
        if (cancelled || !ref.current) return;
        const holeId = holeIds[holeIdx];
        const holeData = holes[holeId];
        const points = holeData?.points || [];
        color.set(holeTraceColor(holeData?.data));
        for (let p = 0; p < points.length - 1; p += 1) {
          const matrix = createHoleMatrixFromPoints(points[p], points[p + 1]);
          ref.current.setMatrixAt(i, matrix);
          colorsArray[i * 3] = color.r;
          colorsArray[i * 3 + 1] = color.g;
          colorsArray[i * 3 + 2] = color.b;
          idata[i] = {
            ...holeData.data,
            holeID: holeData.holeId,
            holeKey: holeData.holeKey || holeId,
            DataSet: holeData.data?.DataSet,
          };
          i += 1;
        }

        if (i % HOLE_UPLOAD_YIELD_BATCH === 0) {
          ref.current.instanceMatrix.needsUpdate = true;
          await yieldToMainThread();
        }
      }

      if (cancelled || !ref.current) return;
      ref.current.count = i;
      ref.current.instanceMatrix.needsUpdate = true;
      fitInstancedMeshBounds(ref.current);
      setInstancesData(idata);
      setColors(i === colorsArray.length / 3 ? colorsArray : colorsArray.slice(0, i * 3));
    };

    uploadHoles();

    return () => {
      cancelled = true;
    };
  }, [holes]);

  const count = Math.max(countHoleSegments(holes), 1);
  const alloc = useGrowingInstanceCount(count);
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, alloc]} userData={{ instancesData }} name="Holes" frustumCulled={false}>
      <cylinderGeometry key={holeRadius} args={[holeRadius, holeRadius, 1, 8]}>
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </cylinderGeometry>
      <meshBasicMaterial vertexColors />
    </instancedMesh>
  );
}

function Intervals({ count, holes, field, name }) {
  const ref = useRef();
  const [colors, setColors] = useState(new Float32Array());
  const [instancesData, setInstancesData] = useState([]);
  const radii = useHoleDisplayRadii();
  const radius = field === 'liths' ? radii.lith : radii.interval;
  const thresholds = useGradePaletteStore((s) => s.thresholds);
  const hiddenLithCodes = useHoleDisplayStore((s) => s.hiddenLithCodes);

  useEffect(() => {
    let cancelled = false;

    const uploadIntervals = async () => {
      if (!ref.current) return;
      const colorsArray = new Float32Array(count * 3);
      const idata = new Array(count);
      let i = 0;

      const holeIds = Object.keys(holes);
      for (let holeIdx = 0; holeIdx < holeIds.length; holeIdx += 1) {
        const intervals = holes[holeIds[holeIdx]]?.[field] || [];
        for (let intervalIdx = 0; intervalIdx < intervals.length; intervalIdx += 1) {
          if (cancelled || !ref.current) return;
          const interval = intervals[intervalIdx];
          const isLith = field === 'liths' || interval.data?.source === 'Geology' || interval.data?.lith1_code != null;
          if (isLith && !isLithCodeVisible(interval.data?.lith1_code, hiddenLithCodes)) continue;
          const gradeValue = Number(interval.data?.gradeValue ?? parseFloat(interval.data?.grade));
          if (!isLith && Number.isFinite(gradeValue) && !isGradeBinVisible(gradeValue, thresholds)) continue;
          ref.current.setMatrixAt(i, interval.matrix);
          const color = isLith
            ? interval.color
            : Number.isFinite(gradeValue)
              ? getAssayColor(gradeValue, thresholds)
              : interval.color;
          const colorOffset = i * 3;
          colorsArray[colorOffset] = color.r;
          colorsArray[colorOffset + 1] = color.g;
          colorsArray[colorOffset + 2] = color.b;
          idata[i] = interval.data;
          i += 1;

          if (i % INTERVAL_UPLOAD_YIELD_BATCH === 0) {
            ref.current.instanceMatrix.needsUpdate = true;
            await yieldToMainThread();
          }
        }
      }

      if (cancelled || !ref.current) return;
      ref.current.count = i;
      ref.current.instanceMatrix.needsUpdate = true;
      fitInstancedMeshBounds(ref.current);
      setInstancesData(i === idata.length ? idata : idata.slice(0, i));
      setColors(i === count ? colorsArray : colorsArray.slice(0, i * 3));
    };

    uploadIntervals();

    return () => {
      cancelled = true;
    };
  }, [holes, count, thresholds, field, hiddenLithCodes]);

  const alloc = useGrowingInstanceCount(Math.max(count, 1));
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, alloc]} userData={{ instancesData }} name={name} frustumCulled={false}>
      <cylinderGeometry key={radius} args={[radius, radius, 1, 8]}>
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </cylinderGeometry>
      <meshLambertMaterial vertexColors toneMapped={false} />
    </instancedMesh>
  );
}
